/*
  Project: Vivafloors
  Author: Thomas
 */

/**
 * Drives the two steps of the 'Mijn stalen' page. Both panels are rendered on
 * page load and only toggled here, because wc-checkout.js binds to the form on
 * load and would not update the order review if the form arrived later.
 */

const STEP_HASH = '#bezorgadres';

function panels(root) {
  return Array.prototype.slice.call(root.querySelectorAll('[data-panel]'));
}

function setStep(root, step, { push = true } = {}) {
  root.setAttribute('data-step', String(step));

  panels(root).forEach(panel => {
    panel.hidden = Number(panel.dataset.panel) !== step;
  });

  // The bar is a sibling of the flow, so walk up to the page to find it.
  const bar = document.querySelector('.c-checkout__steps');
  if (bar) {
    // The panel you are on marks the steps before it as finished: choosing
    // samples is already done by the time the page opens, so step 1 carries a
    // tick from the start, exactly as in the design.
    const items = Array.prototype.slice.call(bar.children);
    const tick = bar.querySelector('.is-done .c-steps-bar__marker svg');

    items.forEach((item, index) => {
      const done = index < step;

      item.classList.toggle('is-done', done);
      item.classList.toggle('is-current', index === step);

      // steps-bar.php only renders the tick for steps that were done on
      // load, so a step finished here swaps its number for step 1's tick,
      // and gets the number back on the way back.
      const marker = item.querySelector('.c-steps-bar__marker');
      if (!marker || !tick) return;

      if (done && !marker.querySelector('svg')) {
        marker.dataset.number = marker.textContent.trim();
        marker.innerHTML = tick.outerHTML;
      } else if (!done && marker.dataset.number) {
        marker.textContent = marker.dataset.number;
        delete marker.dataset.number;
      }
    });
  }

  if (push) {
    const url = step === 2
      ? window.location.pathname + window.location.search + STEP_HASH
      : window.location.pathname + window.location.search;
    window.history.replaceState({ step }, '', url);
  }

  // offsetTop is relative to the offset parent, so measure against the page.
  const top = root.getBoundingClientRect().top + window.pageYOffset - 120;
  window.scrollTo({ top, behavior: 'smooth' });
}

// WooCommerce 11 marks required fields with aria-required, not with the HTML
// required attribute, so the browser's own validation never fires on them.
function isEmpty(field) {
  if (field.type === 'checkbox') {
    return !field.checked;
  }

  if (field.type === 'radio') {
    const group = field.form || document;
    return !group.querySelector('input[name="' + field.name + '"]:checked');
  }

  return !String(field.value || '').trim();
}

const ERROR_CLASS = 'checkout-inline-error-message';
const INVALID_CLASSES = [
  'woocommerce-invalid',
  'woocommerce-invalid-required-field',
  'woocommerce-invalid-email',
  'woocommerce-invalid-phone',
];

function isVisible(element) {
  return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
}

function clearFieldError(row) {
  row.classList.remove(...INVALID_CLASSES);

  const message = row.querySelector('.' + ERROR_CLASS);
  if (message) message.remove();

  Array.prototype.slice.call(row.querySelectorAll('[aria-invalid]')).forEach(field => {
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
  });
}

// Same markup WooCommerce uses for its own inline errors, so its checkout.js
// clears the message again as soon as the visitor fixes the field.
function showFieldError(row, field, text) {
  row.classList.add('woocommerce-invalid');

  let message = row.querySelector('.' + ERROR_CLASS);
  if (!message) {
    message = document.createElement('p');
    message.className = ERROR_CLASS;
    row.appendChild(message);
  }

  message.id = (field.id || field.name) + '_description';
  message.textContent = text;

  field.setAttribute('aria-invalid', 'true');
  field.setAttribute('aria-describedby', message.id);
}

/**
 * Marks every visible required field that is empty, and email and phone
 * fields WooCommerce rejects, in red with a message underneath. Returns the
 * fields in form order, so the caller can move to the first one.
 *
 * WooCommerce 11 marks required fields with aria-required, not with the HTML
 * required attribute, so the browser's own validation never fires on them.
 */
function validateFields(form, messages) {
  const invalid = [];
  const rows = Array.prototype.slice.call(form.querySelectorAll('.form-row'));

  rows.filter(isVisible).forEach(row => {
    const field = row.querySelector('input:not([type="hidden"]), select, textarea');
    if (!field) return;

    // Lets WooCommerce run its own email and phone checks on the field.
    if (window.jQuery && field.type !== 'radio') {
      window.jQuery(field).trigger('validate');
    }

    const required = row.classList.contains('validate-required') ||
      field.getAttribute('aria-required') === 'true';

    let text = '';
    if (required && isEmpty(field)) {
      text = messages.required;
    } else if (row.classList.contains('woocommerce-invalid-email')) {
      text = messages.email;
    } else if (row.classList.contains('woocommerce-invalid-phone')) {
      text = messages.phone;
    }

    if (text) {
      showFieldError(row, field, text);
      invalid.push(field);
    } else {
      clearFieldError(row);
    }
  });

  return invalid;
}

// The ticked cards are what gets ordered.
function orderedCount() {
  return document.querySelectorAll('.c-selection__card.is-selected').length;
}

/**
 * Ticking samples on step 1 (templates/components/checkout-selection.php).
 * The ticked ones are the cart: every change is sent to the server, which
 * sets the cart to exactly those, and update_checkout then redraws the order
 * summary (inc/functions/selection.php). The last change wins; an earlier
 * request still on its way is dropped.
 */
function initSelection(root, onChange) {
  const selection = root.querySelector('.js-selection');
  if (!selection || !window.jQuery) return;

  const $ = window.jQuery;
  const max = Number(selection.dataset.max) || 3;
  const counter = selection.querySelector('.js-selection-counter');
  const limit = selection.querySelector('.js-selection-limit');
  const empty = selection.querySelector('.js-selection-empty');
  const boxes = () => Array.prototype.slice.call(selection.querySelectorAll('.js-selection-checkbox'));
  let request = null;

  const render = () => {
    const ticked = boxes().filter(box => box.checked);

    boxes().forEach(box => {
      box.closest('.js-selection-card').classList.toggle('is-selected', box.checked);
    });

    if (counter) {
      counter.textContent = selection.dataset.counter.replace('%1$d', ticked.length);
    }

    if (ticked.length && empty) empty.hidden = true;
    onChange(ticked.length);

    return ticked;
  };

  const save = ticked => {
    if (request) request.abort();

    root.classList.add('is-updating');

    request = $.ajax({
      type: 'POST',
      dataType: 'json',
      url: window.location.protocol + '//' + window.location.host + '/wp-admin/admin-ajax.php',
      data: {
        action: 'vivafloors_set_selection',
        product_ids: ticked.map(box => box.value),
      },
    }).done(() => {
      $(document.body).trigger('update_checkout');
    }).always((data, status) => {
      if (status === 'abort') return;

      request = null;
      root.classList.remove('is-updating');
    });
  };

  selection.addEventListener('change', event => {
    const box = event.target.closest('.js-selection-checkbox');
    if (!box) return;

    if (box.checked && boxes().filter(other => other.checked).length > max) {
      box.checked = false;
      if (limit) limit.hidden = false;
      return;
    }

    if (limit) limit.hidden = true;
    save(render());
  });

  render();
}

function initCheckoutSteps() {
  const root = document.querySelector('.js-checkout');
  if (!root) return;

  // header.php and custom_gform_init() in functions.php both load the bundle.
  // Bound twice, one click on step 1 would open step 2 and at once run its
  // check too, turning every empty field red before anything was typed.
  if (root.dataset.stepsBound) return;
  root.dataset.stepsBound = 'true';

  const advance = root.querySelector('.js-checkout-advance');
  const messages = {
    required: root.dataset.msgRequired || '',
    email: root.dataset.msgEmail || root.dataset.msgRequired || '',
    phone: root.dataset.msgPhone || root.dataset.msgRequired || '',
  };

  // WooCommerce clears its inline errors on input, but not on the radio
  // questions, which it doesn't validate itself.
  root.addEventListener('change', event => {
    if (event.target.type !== 'radio') return;

    const row = event.target.closest('.form-row');
    if (row) clearFieldError(row);
  });

  const setAdvanceState = count => {
    if (!advance || Number(root.getAttribute('data-step')) !== 1) return;
    advance.classList.toggle('is-inactive', count === 0);
    advance.setAttribute('aria-disabled', String(count === 0));
  };

  initSelection(root, setAdvanceState);

  // The heart drops the product from the favorites. The theme hides the
  // plugin's own button, so hand the click to it and let the plugin do the
  // work, the same way the popup adds a favorite.
  root.addEventListener('click', event => {
    const trigger = event.target.closest('.js-remove-favorite');
    if (!trigger) return;

    event.preventDefault();

    const card = trigger.closest('.c-selection__card');
    const pluginButton = card && card.querySelector('.simplefavorite-button');
    if (!pluginButton) return;

    card.classList.add('is-removing');
    pluginButton.dispatchEvent(new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }));
  });

  // page-checkout.php drops a removed favorite from the cart on load, so a
  // reload is what keeps the selection, the cart and the totals in step after
  // a removal.
  if (window.jQuery) {
    window.jQuery(document).on('favorites-updated-single', () => {
      if (document.querySelector('.c-selection__card.is-removing')) {
        window.location.reload();
      }
    });
  }

  if (advance) {
    advance.addEventListener('click', () => {
      const step = Number(root.getAttribute('data-step'));

      if (step === 1) {
        if (orderedCount() === 0) {
          const empty = root.querySelector('.js-selection-empty');
          if (empty) empty.hidden = false;
          return;
        }

        // Wait for the last tick to reach the cart, or the address step
        // would open on a stale order summary.
        if (root.classList.contains('is-updating')) return;

        advance.classList.remove('is-inactive');
        advance.removeAttribute('aria-disabled');
        setStep(root, 2);
        return;
      }

      // On step 2 the visible button drives WooCommerce's own submit, which
      // lives inside the form and is hidden. Never reach for it while the
      // address panel is still closed.
      const details = root.querySelector('[data-panel="2"]');
      if (!details || details.hidden) return;

      // Mark the fields themselves rather than letting the server answer with
      // a wall of validation notices at the top of the form.
      const form = root.querySelector('form.checkout');
      const invalid = form ? validateFields(form, messages) : [];

      if (invalid.length) {
        invalid[0].focus({ preventScroll: true });
        invalid[0].closest('.form-row').scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }

      const placeOrder = root.querySelector('#place_order');
      if (placeOrder) placeOrder.click();
    });
  }

  // A refresh or a back navigation should land where the visitor left off.
  // Only the hash may decide this: WooCommerce keeps notices in the session,
  // so an unrelated leftover notice would otherwise open the address panel and
  // turn the very first click into a place-order.
  if (window.location.hash === STEP_HASH) {
    setStep(root, 2, { push: false });
  }

  // A failed submit is an AJAX round trip, so the panel is already open; this
  // only covers a server-side re-render that dropped the hash.
  if (window.jQuery) {
    window.jQuery(document.body).on('checkout_error', () => {
      if (Number(root.getAttribute('data-step')) !== 2) {
        setStep(root, 2);
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', initCheckoutSteps);



// WEBPACK FOOTER //
// ./src/scripts/modules/checkoutSteps.js